import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "../../components/AppShell";
import { Users, Home, GraduationCap, Calendar, TrendingUp, DollarSign, AlertCircle, ClipboardList, ArrowRight } from "lucide-react";
import api from "../../lib/api";

const METRICS = [
  { key: "active_members", label: "Active Members", icon: Users },
  { key: "active_families", label: "Active Families", icon: Home },
  { key: "total_students", label: "Total Students", icon: GraduationCap },
  { key: "upcoming_experiences", label: "Upcoming", icon: Calendar },
  { key: "mrr", label: "MRR", icon: TrendingUp, money: true },
  { key: "month_revenue", label: "This Month", icon: DollarSign, money: true },
  { key: "outstanding_payments", label: "Outstanding", icon: AlertCircle },
  { key: "registrations", label: "Registrations", icon: ClipboardList },
];

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [metrics, setMetrics] = useState({});
  const [experiences, setExperiences] = useState([]);
  const [overviews, setOverviews] = useState({});

  useEffect(() => {
    api.get("/admin/dashboard").then((r) => setMetrics(r.data)).catch(() => {});
    api.get("/experiences?include_hidden=true").then((r) => {
      const up = r.data.filter((e) => new Date(e.date) >= new Date());
      setExperiences(up);
      up.forEach((e) => api.get(`/admin/experiences/${e.id}/overview`).then((o) => setOverviews((prev) => ({ ...prev, [e.id]: o.data }))).catch(() => {}));
    }).catch(() => {});
  }, []);

  return (
    <AppShell admin>
      <h1 className="font-display text-3xl font-bold text-navy">Operations Dashboard</h1>
      <p className="text-slateBrand">Everything you need to run H.E.A. from one place.</p>

      <div className="mt-8 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {METRICS.map((m) => (
          <div key={m.key} data-testid={`admin-metric-${m.key}`} className="rounded-xl border border-slate-200 bg-white p-5">
            <div className="flex items-center gap-2 text-slateBrand"><m.icon className="h-4 w-4" /><span className="text-xs">{m.label}</span></div>
            <div className="mt-2 font-display text-2xl font-bold text-navy">{m.money ? `$${(metrics[m.key] ?? 0).toLocaleString()}` : (metrics[m.key] ?? 0)}</div>
          </div>
        ))}
      </div>

      <h2 className="mt-12 font-display text-xl font-bold text-navy">Upcoming Experiences</h2>
      <div className="mt-5 space-y-5">
        {experiences.map((e) => {
          const o = overviews[e.id] || {};
          return (
            <div key={e.id} data-testid={`admin-exp-card-${e.id}`} className="rounded-2xl border border-slate-200 bg-white p-6">
              <div className="flex flex-col justify-between gap-4 md:flex-row md:items-start">
                <div>
                  <div className="font-display text-lg font-bold text-navy">{e.title}</div>
                  <div className="text-xs text-slateBrand">{new Date(e.date).toLocaleDateString("en-US", { month: "long", day: "numeric" })} • {e.location}</div>
                </div>
                <div className="text-right">
                  <div className="font-display text-xl font-bold text-successBrand">${(o.revenue ?? 0).toLocaleString()}</div>
                  <div className="text-xs text-slateBrand">Est. margin ${(o.estimated_margin ?? 0).toLocaleString()}</div>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-3 gap-2 text-center sm:grid-cols-6">
                {[
                  ["Registered", o.registered], ["Paid", o.paid], ["Transport", o.transport_riders],
                  ["Drivers", o.parent_drivers], ["Chaperones", o.chaperones], ["Forms Missing", o.forms_missing],
                ].map(([l, v]) => (
                  <div key={l} className="rounded-lg bg-cloud p-2">
                    <div className="font-display font-bold text-navy">{v ?? 0}</div>
                    <div className="text-[0.65rem] text-slateBrand">{l}</div>
                  </div>
                ))}
              </div>
              {o.accommodation_alerts > 0 && (
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-alertBrand/5 px-3 py-2 text-xs text-alertBrand"><AlertCircle className="h-3.5 w-3.5" />{o.accommodation_alerts} important accommodation alert(s)</div>
              )}
              <div className="mt-4 flex justify-end">
                <button data-testid={`admin-manage-exp-${e.id}`} onClick={() => navigate(`/admin/experiences/${e.id}`)} className="inline-flex items-center gap-1 font-display text-sm font-semibold text-brandblue">Manage Experience <ArrowRight className="h-4 w-4" /></button>
              </div>
            </div>
          );
        })}
        {experiences.length === 0 && <p className="text-sm text-slateBrand">No upcoming experiences. Create one to get started.</p>}
      </div>
    </AppShell>
  );
}
